import type { OrgPartner } from "@kontaktfix/shared";
import { Badge, Button, Card, CardHeader, Dialog, Field, Input, Skeleton } from "@kontaktfix/ui";
import { HandshakeIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatDate } from "@/lib/i18n";
import { keys, useMe } from "@/lib/queries";

const partnersKey = ["partners"] as const;

/** Settings, Partner: IT partners with access to this organization (issue #27). */
export function PartnerTab() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const client = useQueryClient();
  const { data: partners, isPending } = useQuery({
    queryKey: partnersKey,
    queryFn: () => api.get<OrgPartner[]>("/api/partners"),
  });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState<OrgPartner | null>(null);
  const isAdmin = me?.role === "admin";
  // Partner technicians see the list but the decision stays with the customer.
  const canDecide = isAdmin && !me?.partner;

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: partnersKey }),
      client.invalidateQueries({ queryKey: keys.members }),
    ]);

  const link = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/partners", { code });
      setCode("");
      toast.success(t("settings.partner.linked"));
      await refresh();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const approve = async (p: OrgPartner) => {
    try {
      await api.post(`/api/partners/${p.id}/approve`);
      toast.success(t("settings.partner.approved"));
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async (p: OrgPartner) => {
    setRevoking(null);
    try {
      await api.delete(`/api/partners/${p.id}`);
      toast.success(p.status === "pending" ? t("settings.partner.declined") : t("settings.partner.revoked"));
      await refresh();
      // A partner technician who removed their own partner has no access left.
      if (me?.partner) await client.invalidateQueries({ queryKey: keys.me });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Card>
      <CardHeader title={t("settings.partner.title")} description={t("settings.partner.text")} />
      {isPending ? (
        <div className="p-5">
          <Skeleton className="h-12" />
        </div>
      ) : (
        <div className="divide-y divide-border">
          {!partners?.length && (
            <div className="flex items-center gap-2.5 px-5 py-4 text-[13px] text-fg-muted">
              <HandshakeIcon className="size-4" />
              {t("settings.partner.empty")}
            </div>
          )}
          {partners?.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[13px] font-medium">
                  {p.partnerName}
                  <Badge tone={p.status === "active" ? "success" : "warning"}>
                    {t(`settings.partner.${p.status}`)}
                  </Badge>
                </div>
                <div className="text-[12px] text-fg-muted">
                  {p.status === "active"
                    ? t("settings.partner.since", { date: formatDate(p.approvedAt ?? p.createdAt) })
                    : t("settings.partner.requested", { date: formatDate(p.createdAt) })}
                </div>
              </div>
              {p.status === "pending" && canDecide && (
                <Button size="sm" variant="primary" onClick={() => approve(p)}>
                  {t("settings.partner.approve")}
                </Button>
              )}
              {isAdmin && (p.status === "active" || canDecide) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => (p.status === "pending" ? remove(p) : setRevoking(p))}
                >
                  {p.status === "pending" ? t("settings.partner.decline") : t("settings.partner.revoke")}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {canDecide ? (
        <form onSubmit={link} className="flex flex-wrap items-end gap-3 border-t border-border px-5 py-4">
          <Field
            label={t("settings.partner.code")}
            htmlFor="partner-code"
            hint={t("settings.partner.codeHint")}
          >
            <Input
              id="partner-code"
              required
              maxLength={8}
              className="w-40 font-mono uppercase tracking-wider"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </Field>
          <Button type="submit" loading={busy} disabled={code.trim().length !== 8}>
            {t("settings.partner.link")}
          </Button>
        </form>
      ) : (
        me?.partner && (
          <p className="border-t border-border px-5 py-3 text-[12.5px] text-fg-muted">
            {t("settings.partner.partnerOnly")}
          </p>
        )
      )}
      <Dialog
        open={revoking !== null}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={t("settings.partner.revokeTitle", { name: revoking?.partnerName })}
        description={t("settings.partner.revokeText")}
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={() => revoking && remove(revoking)}>
              {t("settings.partner.revoke")}
            </Button>
          </>
        }
      />
    </Card>
  );
}
